import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  EventEmitter,
  Input, OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { SessionRow, SessionsProvider } from '../../../sessions/providers/sessions-provider';
import {
  CalendarDayData,
  CalendarDaySegment,
  TimeslotRow,
  TimeslotsProvider,
  WeekForDuplication
} from '../../providers/timeslots-provider';
import { AgendasRepeatSelectorComponent } from '../repeat-selector/repeat-selector';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TimeslotsChangesProvider } from '../../providers/timeslots-changes-provider';
import { ConfirmDeleteSessionFormComponent } from '../confirm-delete-session/confirm-delete-session';
import { ActivatedRoute } from '@angular/router';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { ErrorManagerService } from '../../../common/providers/error-manager-service';
import { SessionItemRow, SessionItemsProvider } from '../../../sessions/providers/sessionItems-provider';
import moment from 'moment';
import { Observable, Subscription, map, forkJoin, of, switchMap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Agenda } from "../../../common/models/agenda";
import { Organization } from "../../../common/models/organization";
import { Athlete } from "../../../common/models/athlete";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { DurationVerbosePipe } from "../../../common/pipes/duration-verbose";
import { AppDatePipe } from "../../../common/pipes/app-date";
import { ApiBaseService } from "../../../common/providers/api-base-service";
import { CopyPasteBufferService } from "../../../common/providers/copy-paste-buffer-service";
import { Session } from "../../../common/models/session";
import { Timeslot } from "../../../common/models/timeslot";
import { ConfirmDialogComponent } from "../../../common/components/confirm-dialog/confirm-dialog";
import { FlexLayoutModule } from "@angular/flex-layout";
import { NgForOf, NgIf } from "@angular/common";
import { MatButton, MatButtonModule, MatIconButton, MatMiniFabButton } from "@angular/material/button";
import { MatTooltip, MatTooltipModule } from "@angular/material/tooltip";
import { SecondsToDHMPipe } from "../../../common/pipes/seconds-to-DHM-pipe";
import { DateClosestPipe } from "../../../common/pipes/date-closest";
import { DefaultPipe } from "../../../common/pipes/default";
import { MatIcon, MatIconModule } from "@angular/material/icon";
import { AgendasWeekSelectorComponent } from "../week-selector/week-selector";
import { NgPipesModule } from "ngx-pipes";
import { CdkDrag, CdkDragDrop, DragDropModule } from "@angular/cdk/drag-drop";
import { AgendasCalendarEventComponent } from "../calendar-event/calendar-event";
import { ExercicesProvider } from "../../../exercices/providers/exercices-provider";
import { catchError } from "rxjs/operators";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { User } from "../../../common/models/user";

@Component({
  selector: 'agendas-calendar',
  providers: [],
  styleUrls: ['./calendar.scss'],
  templateUrl: './calendar.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FlexLayoutModule,
    MatMenuModule,
    MatButtonModule,
    NgIf,
    NgForOf,
    MatIconButton,
    MatMiniFabButton,
    MatTooltipModule,
    TranslateModule,
    AppDatePipe,
    DateClosestPipe,
    SecondsToDHMPipe,
    DefaultPipe,
    MatIconModule,
    AgendasWeekSelectorComponent,
    NgPipesModule,
    DragDropModule,
    AgendasCalendarEventComponent,
    MatCheckboxModule,
  ],
  standalone: true
})
export class AgendasCalendarComponent implements OnInit, OnDestroy {

  @Input()
  public set agenda(agenda: Agenda) {
    if (this.agenda === agenda) {
      return;
    }

    this._agenda = agenda;
  }

  public get agenda() {
    return this._agenda;
  }

  @Input()
  public set currentDate(date: Date) {
    this._currentDate = date;
    this.currentDateChange.emit(date);
  }

  public get currentDate() {
    return this._currentDate;
  }

  @Input()
  public set data(data: CalendarDayData[]) {
    this._data = data;
    this.updateDroppedStuff();
    this.computeSummary();
  }

  public get data() {
    return this._data;
  }

  @ViewChild(MatMenuTrigger, {static: false})
  public contextMenu: MatMenuTrigger = {} as MatMenuTrigger;
  public contextMenuPosition = {x: '0px', y: '0px'};
  public collapseHeight = 4;


  @Input() public organization: Organization | null = null;
  @Output() public currentDateChange: EventEmitter<Date> = new EventEmitter<Date>();
  @Input() public timeSegments = 2;
  @Input() public lineHeight = 48;
  @Input() public collapseBefore: string | null = null;
  @Input() public collapseAfter: string | null = null;
  @Input() public isSummaryHidden = false;
  @Input() public availableTime = 0;
  @Input() public availableTimePercent = 0;
  @Input() public availableIntensity = 0;
  @Output() public onSegmentChanged: EventEmitter<CalendarDaySegment> = new EventEmitter<CalendarDaySegment>();
  @Input() public beforeDateChangeHook$:any = null;
  @Input() public isEditable = false;
  @Input() public isDuplicateModActive = false;
  @Input() public timeslotRowsSelectedForDuplication: TimeslotRow[] = [];
  @Output() public isDuplicateModActiveChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public onSessionDuplicateCheckboxSelectedChange: EventEmitter<any> = new EventEmitter<any>();

  @Input() public athleteUser: User = {} as User;
  @Output() public onCreateNewSession: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public onSessionExport: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() onRefresh = new EventEmitter<void>();

  public totalIntensity = 0;
  public summaryIntensity = '';
  public totalTime = 0;
  public displayTime = false;
  public deleteDialog: MatDialogRef<ConfirmDeleteSessionFormComponent> | undefined;

  public currentLocale: string;

  public droppedStuff: any[] = [];
  public currentUrl?: string = undefined;
  private _agenda: Agenda = {} as Agenda;
  private _currentDate: Date = new Date();
  private _data: CalendarDayData[] = [];

  private _virtualDecrement = -1000;
  private _nextGeneratedId = 1;
  private _draggedTimeslotReferenceId = '';
  private subscriptions: Subscription[] = [];
  constructor(
    public utils: UtilsProvider,
    public timeslotsProvider: TimeslotsProvider,
    public timeslotsChangesProvider: TimeslotsChangesProvider,
    public translator: TranslateService,
    private _toaster: MatSnackBar,
    private _sessionsProvider: SessionsProvider,
    private _sessionItemsProvider: SessionItemsProvider,
    private _dialog: MatDialog,
    private _translater: TranslateService,
    private _activatedRoute: ActivatedRoute,
    public copyPasteBuffer: CopyPasteBufferService,
    private errorManagerService: ErrorManagerService,
    private ts: TranslateService,
    private exercicesProvider: ExercicesProvider,
    private cdr: ChangeDetectorRef,
  ) {
    this.currentLocale = this.ts.currentLang;
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      this._activatedRoute.url.subscribe((url) => {
        this.currentUrl = url.join('/');
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public onExpand(segment: CalendarDaySegment, event: MouseEvent): void {
    event.stopPropagation();
    if (this.isSegmentCollapsed(segment)) {
      this.collapseBefore = null;
      this.collapseAfter = null;
      this.cdr.markForCheck();
    }
  }

  public collapse(segment: CalendarDaySegment, direction: 'before' | 'after', event: MouseEvent): void {
    event.stopPropagation();
    if (direction === 'before') {
      this.collapseBefore = segment.armyTimeStr;
    } else if (direction === 'after') {
      const date: Date = new Date(segment.time);
      date.setUTCMinutes(59);
      this.collapseAfter = this.utils.DateToHm(date);
    }
  }

  public onDragEntered(segment: CalendarDaySegment): void {
    if (this.isSegmentVisible(segment)) {
      segment.displayTime = true;
    }
  }

  public onDragExited(segment: CalendarDaySegment): void {
    segment.displayTime = false;
  }

  public isSegmentCollapsed(segment: CalendarDaySegment): boolean {
    const beforeCollapsed = this.collapseBefore !== null && segment.armyTimeStr < this.collapseBefore;
    const afterCollapsed = this.collapseAfter !== null && segment.armyTimeStr > this.collapseAfter;
    return beforeCollapsed || afterCollapsed;
  }

  public isSegmentVisible(segment: CalendarDaySegment): boolean {
    const notBeforeCollapsed = this.collapseBefore === null || segment.armyTimeStr >= this.collapseBefore;
    const notAfterCollapsed = this.collapseAfter === null || segment.armyTimeStr <= this.collapseAfter;
    return notBeforeCollapsed && notAfterCollapsed;
  }

  public getSegmentHeight(segment: CalendarDaySegment): number {
    return this.isSegmentCollapsed(segment) ? this.collapseHeight : this.lineHeight;
  }

  public onItemDropped(event: CdkDragDrop<any>): void {
    this.onCreateNewSession.emit(true);

    const segment = event.container.data; // get target segment

    const sessionData = event.item.data; // Access the original data

    let sessionId: number = sessionData.calendarGeneratedId?.sessionId ?? sessionData.sessionId;
    const generatedId: number | undefined = sessionData.calendarGeneratedId?.id;

    /**
     * Création d'un Timeslot virtuel lié à la séance qu'on dépose.
     * Elle est virtuelle car elle n'existe qu'en mémoire. Elle sert
     * plutard à déterminer si on peut "poser" une séance à cet
     * endroit.
     */
    const timeslot: Timeslot = new Timeslot({
      sessionId,
      agendaId: this.agenda!.id,
      fromDate: this.utils.DateToYmdHis(segment.time, false),
    });

    // charge la sessionRow collée
    this._sessionsProvider.getSession({id: sessionId, filled: true}).pipe(
      switchMap((session: Session) => {
        return this._sessionItemsProvider.buildSessionItemRowsFromSession(this.organization!, session)
          .pipe(
            switchMap((sessionItems: SessionItemRow[]) => {
              const sessionRow = this._sessionsProvider.createSessionRow(this.organization!, session, 0);
              /**
               * Peux-t-on poser la séance à cet endroit du slot ? On construit un
               * timeslotRow avec la session qu'on veut ajouter et le timeslot
               * précédemment créé. Si l'espace est occupé, on quitte la procédure
               * en "recalculant" les éléments déposés/existants.
               * Ici, on omet volontairement de récupérer le repetitionTag qui pourrait
               * exister sur le timeslot qu'on veut déplacer (dans le cas où c'est un
               * déplacement). Si on déplace un élément faisant partie d'une séance
               * répétée, celle-ci sort de cette répétition.
               */
              timeslot.session = session;
              const slot: TimeslotRow = {
                session: sessionRow,
                timeslot,
                calendarGeneratedId: null
              };
              if (!this.canAddSlot(segment, slot, this._draggedTimeslotReferenceId)) {
                this.termineAndRefreshCalendarSegment(segment, false);
                this._toaster.open(this.translator.instant('Another_session_at_same_time_error'), '', {duration: 5000});
                this.onCreateNewSession.emit(false);
                return of(null);
              }

              if (generatedId) {

                /**
                 * Si le generatedId existe, il s'agit d'une séance qui
                 * existe dans la semaine. C'est donc un déplacement de
                 * séance. On supprime l'ancien emplacement. Et on ajoute
                 * le nouveau.
                 */

                this.removeSlot(generatedId);
                // replace the session in the segment from this._data
                this.addSlotToData(slot, segment);


                /**
                 * Pour finir le traitement, on mets à zéro la référence de la
                 * séance traitée. On mets à jour les éléments de la semaine.
                 * On calcule les totaux et on averti les autres d'aun changement.
                 */
                this.termineAndRefreshCalendarSegment(segment, true);
                this.onCreateNewSession.emit(false);
                return of(null);
              } else {
                // duplique cette séance comme nouvelle séance planifiée
                return this.createANewSession(sessionRow, sessionItems, segment).pipe(
                  map((sgmt) => {
                    // ajoute le nouveau timeslot au calendrier
                    this.addSlotToData(sgmt, segment);

                    /**
                     * Pour finir le traitement, on mets à zéro la référence de la
                     * séance traitée. On mets à jour les éléments de la semaine.
                     * On calcule les totaux et on averti les autres d'aun changement.
                     */
                    this.termineAndRefreshCalendarSegment(segment, true);
                    this.onCreateNewSession.emit(false);
                  }),
                );
              }
            })
          );
      }),
    ).subscribe({
      next: () => {
        this.cdr.detectChanges()
      },
      error: (error) => {
        console.error("ERROR MOVING SESSION", error);
        this.onCreateNewSession.emit(false);
        this.onError(error);
      },
    });
  }

  private addSlotToData(slot: TimeslotRow, segment: CalendarDaySegment): void {
    this._data.forEach((dayData: CalendarDayData) => {
      dayData.segments.forEach((segmentData: CalendarDaySegment) => {
        if (segmentData === segment) {
          segmentData.slots.push(slot);
        }
      });
    });
  }

  public onRepeatSlotClicked(segment: CalendarDaySegment, slot: TimeslotRow): void {
    const dialogRef: any = this._dialog.open(AgendasRepeatSelectorComponent, {
      data: {
        formTitle: 'Repetition_end',
        confirmButtonTitle: this._translater.instant('Repeat'),
        slot,
      }
    });
    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((endDate: Date) => {
        if (endDate) {

          // get weeks between current date and end date
          const weeks: WeekForDuplication[] = [];
          const today = new Date(segment.time);
          const toDate = new Date(endDate);

          while (today.getTime() < toDate.getTime() ||
            (today.getTime() <= toDate.getTime() && today.getDay() <= toDate.getDay())) {
            weeks.push({
              name: this.utils.getWeekNumber(today) + ' - ' + today.getFullYear(),
              number: this.utils.getWeekNumber(today),
              fromDate: this.utils.DateToYmd(this.utils.firstDayOfWeek(today))
            });
            today.setDate(today.getDate() + 7);
          }

          this.timeslotsProvider.duplicateTimeslotsOnWeeks({
            organization: this.organization!,
            agenda: this.agenda,
            timeslotRows: [slot],
            weeks: weeks,
            overwriteOverlapping: true,
          }).subscribe({
            next: () => {
              this._toaster.open(this.translator.instant('Session_duplicated'), '', {duration: 5000});
            },
            error: (error) => {
              this.onError(error);
            }
          });
        }
      }),
    );
  }

  public onDuplicateSlotClicked(segment: CalendarDaySegment, slot: TimeslotRow): void {
    this.copyPasteBuffer.copy({sessionId: slot.session!.id!});
  }

  public onOpenContextMenu(event: any, segment: CalendarDaySegment) {
    event.preventDefault();

    if (!this.isSegmentVisible(segment)) {
      return;
    }

    if (this.isEditable) {

      // ouvre si l'élément cliqué est un "slots-container"
      if (event.srcElement.className.indexOf('slots-container') > -1 && this.copyPasteBuffer.containSomething()) {

        this.contextMenuPosition.x = event.clientX + 'px';
        this.contextMenuPosition.y = event.clientY + 'px';
        this.contextMenu.menuData = {item: segment};
        this.contextMenu.menu?.focusFirstItem('mouse');
        this.contextMenu.openMenu();
      }
    }
  }

  public onContextMenuPasteAction(segment: CalendarDaySegment) {

    if (this.isEditable && this.copyPasteBuffer.containSomething()) {

      this.onCreateNewSession.emit(true);
      const pasteObject = this.copyPasteBuffer.paste();

      // charge la sessionRow collée
      forkJoin([
        this._sessionsProvider.getSession({id:pasteObject.sessionId, withTrashed:true}),
        this._sessionItemsProvider.listSessionItemRows(this.organization!, pasteObject.sessionId, true, true),
      ]).pipe(
        switchMap(([session, sessionItems]: [Session, SessionItemRow[]]) => {
          // duplique cette nouvelle séance comme séance planifiée
          const sessionRow = this._sessionsProvider.createSessionRow(this.organization!, session, 0);
          return this.createANewSession(sessionRow, sessionItems, segment).pipe(
            map((newSlot) => {
              // ajoute le nouveau timeslot au calendrier
              if (!newSlot) {
                this.onCreateNewSession.emit(false);
                return;
              }
              segment.slots.push(newSlot);
              /**
               * Pour finir le traitement, on mets à zéro la référence de la
               * séance traitée. On mets à jour les éléments de la semaine.
               * On calcule les totaux et on averti les autres d'aun changement.
               */
              this.termineAndRefreshCalendarSegment(segment, true);
              this.onCreateNewSession.emit(false);

            }),
          );
        }),
      ).subscribe({
        error: (error) => {
          this.onCreateNewSession.emit(false);
          this.onError(error);
        }
      })
    }
  }

  public onDeleteSlotClicked(segment: CalendarDaySegment, slot: TimeslotRow): void {

    if (slot.timeslot!.repetitionTag) {
      this.deleteDialog = this._dialog.open(ConfirmDeleteSessionFormComponent, {
        data: {
          confirmText: this.translator.instant('TimeslotRepetitionDelete'),
          confirmActionText: this.translator.instant('Confirm'),
          color: 'accent'
        }
      });
      this.subscriptions.push(
        this.deleteDialog.afterClosed().subscribe((confirmed: number) => {
          if (confirmed === 1) {
            // supprime la série
            this.timeslotsChangesProvider.addRepetitionTagToRemove(slot.timeslot!.repetitionTag!);
          }
          if (confirmed === 0 || confirmed === 1) {
            // supprime la session
            const slotIndex = segment.slots.indexOf(slot);
            if (slotIndex >= 0) {
              segment.slots.splice(slotIndex, 1);
              this.computeSummary();
              this.onSegmentChanged.emit(segment);
            }
          }
        })
      );
    } else {
      const slotIndex = segment.slots.indexOf(slot);
      if (slotIndex >= 0) {
        segment.slots.splice(slotIndex, 1);
        this.computeSummary();
        this.onSegmentChanged.emit(segment);
      }
    }
  }

  public onDrag(event: any, segment: CalendarDaySegment) {
    this._draggedTimeslotReferenceId = '';
    if (segment.slots && segment.slots[0]) {
      this._draggedTimeslotReferenceId = segment.slots[0].calendarGeneratedId.tag;
    }
  }

  public onEmptyCalendarClicked(): void {
    const dlg = this._dialog.open(ConfirmDialogComponent, {
      data: {
        confirmText: 'Empty_calendar_warning',
        confirmActionText: 'Empty_calendar'
      }
    });
    this.subscriptions.push(
      dlg.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.emptyCalendar();
        }
      })
    );
  }

  public sessionExport() {
    this.onSessionExport.emit(true);
  }

  public onDuplicateModeClicked(): void {
    this.isDuplicateModActiveChange.emit(!this.isDuplicateModActive);
  }

  private createANewSession(sessionRow: SessionRow, sessionItems: SessionItemRow[], segment: CalendarDaySegment, repetitionTag: string = ''): Observable<TimeslotRow> {
    // duplique cette séance comme nouvelle séance planifiée
    sessionRow.libraryId = 0;
    sessionRow.type = Session.SESSION_TYPE_PERSONALIZED;
    sessionRow.id = 0;

    return this._sessionItemsProvider.saveSessionItemRows(this.organization!.id!, 0, sessionItems, sessionRow, sessionRow.availableLangIds).pipe(
      map((newSession) => {
        /**
         * Création d'un timeslot virtuel dans le storage pour lier le tout.
         * Si je n'ai pas ce timeslot virtuel, le service des droits ne peut
         * pas déterminer que j'ai le droit de modifier cette session.
         * L'agenda n'affiche que les Timeslot ayant un ID plus grand que
         * 0. Les Timeslot virtuels ne sont pas affichés.
         */
        const from = moment(segment.time);
        const ts: Timeslot = Timeslot.fromAPI({
          session: newSession,
          id: this._virtualDecrement--,
          sessionId: newSession.id,
          agendaId: this.agenda!.id,
          fromDate: from.format('YYYY-MM-DD HH:mm:ss'),
        });
        if (repetitionTag) {
          ts.repetitionTag = repetitionTag;
        }

        return {
          session: this._sessionsProvider.createSessionRow(this.organization!, newSession, 0),
          timeslot: ts,
          calendarGeneratedId: null
        } as TimeslotRow;
      })
    );
  }

  private termineAndRefreshCalendarSegment(segment: CalendarDaySegment, hasChanged = true) {
    this._draggedTimeslotReferenceId = '';
    this.updateDroppedStuff();
    this.computeSummary();

    if (hasChanged) {
      this.onSegmentChanged.emit(segment);
    }
  }

  private updateDroppedStuff(): void {
    this.droppedStuff = [];
    for (const dayData of this._data) {
      for (const segment of dayData.segments) {
        segment.displayTime = false;
        for (const slot of segment.slots) {
          const droppedItem: any = {
            sessionId: slot.session!.id,
            id: this._nextGeneratedId,
            tag: null
          };
          if (slot.calendarGeneratedId && slot.calendarGeneratedId.tag) {
            droppedItem.tag = slot.calendarGeneratedId.tag;
          } else {
            droppedItem.tag = this.utils.getUniqTag();
          }
          slot.calendarGeneratedId = droppedItem;
          this.droppedStuff.push(droppedItem);
          this._nextGeneratedId++;
        }
      }
    }
    this.cdr.detectChanges();
  }

  private removeSlot(generatedId: number): void {
    for (const dayData of this._data) {
      for (const segment of dayData.segments) {
        for (let i = 0; i < segment.slots.length; i++) {
          if (segment.slots[i].calendarGeneratedId.id === generatedId) {
            segment.slots.splice(i, 1);
            return;
          }
        }
      }
    }
  }

  private computeSummary(): void {
    let totalIntensity = 0;
    let sessionsCount = 0;
    let totalTime = 0;
    this.data.forEach((row: CalendarDayData) => {
      row.segments.forEach((segment: CalendarDaySegment) => {
        segment.slots.forEach((slot: TimeslotRow) => {
          totalIntensity += slot.session!.intensity!;
          totalTime += slot.session!.totalTime!;
          // sessions with intensity of 0% are exlcuded from the average
          if (slot.session!.intensity) {
            sessionsCount++;
          }
        });
      });
    });

    this.totalIntensity = sessionsCount > 0 ? Math.round(totalIntensity / sessionsCount) : 0;
    this.totalTime = totalTime;
  }

  private getSegmentMatrix(segment: CalendarDaySegment): { i: number; j: number } {
    for (let i = 0; i < this.data.length; i++) {
      for (let j = 0; j < this.data[i].segments.length; j++) {
        if (this.data[i].segments[j] === segment) {
          return {
            i,
            j
          };
        }
      }
    }
    return {i: 0, j: 0};
  }

  private canAddSlot(segment: CalendarDaySegment, slot: TimeslotRow, tag: string): boolean {
    const segmentsCount = this.getSegmentsCount([slot]);
    const segmentMatrix = this.getSegmentMatrix(segment);
    const maxJ = segmentMatrix.j + segmentsCount;
    for (let j = 0; j < this.data[segmentMatrix.i].segments.length; j++) {
      if (this.data[segmentMatrix.i].segments[j] !== segment) {

        let existingTag = '-def-tag';
        if (this.data[segmentMatrix.i].segments[j].slots.length) {
          existingTag = this.data[segmentMatrix.i].segments[j].slots[0].calendarGeneratedId.tag;
        }
        if (existingTag !== tag) {
          const checkSegmentsCount = this.getSegmentsCount(this.data[segmentMatrix.i].segments[j].slots);
          if (checkSegmentsCount) {
            const checkMaxJ = j + checkSegmentsCount;
            if (!(checkMaxJ <= segmentMatrix.j || j >= maxJ)) {
              return false;
            }
          }
        }
      }
    }

    return true;
  }

  private getNextAvailableSegment(segment: CalendarDaySegment): CalendarDaySegment {
    const segmentsCount = this.getSegmentsCount(segment.slots);
    const segmentMatrix = this.getSegmentMatrix(segment);
    segmentMatrix.j += segmentsCount;

    while (true) {
      if (segmentMatrix.j >= this.data[segmentMatrix.i].segments.length) {
        segmentMatrix.j = 0;
        segmentMatrix.i++;
        if (segmentMatrix.i >= this.data.length) {
          return {} as CalendarDaySegment;
        }
      }

      const nextItemSegmentsCount = this.getSegmentsCount(this.data[segmentMatrix.i].segments[segmentMatrix.j].slots);
      if (nextItemSegmentsCount === 0) {
        return this.data[segmentMatrix.i].segments[segmentMatrix.j];
      }

      segmentMatrix.j += nextItemSegmentsCount;
    }
  }

  private getSegmentsCount(slots: TimeslotRow[]): number {
    const slotDuration = 3600 / this.timeSegments;
    let longestSession = 0;
    for (const slot of slots) {
      longestSession = (slot.session!.totalTime! > longestSession ? slot.session!.totalTime : longestSession) ?? 0;
    }

    return Math.ceil(longestSession / slotDuration);
  }

  private emptyCalendar(): void {
    for (const dayData of this.data) {
      for (const segmentData of dayData.segments) {
        segmentData.slots = [];
      }
    }
    this.data = this.data.slice();
    this.computeSummary();
    this.onSegmentChanged.emit(this.data[0].segments[0]);
  }


  public onSlotSelectedChanged(event: any, slot: TimeslotRow) {
    event.preventDefault();
    event.stopPropagation();
    this.onSessionDuplicateCheckboxSelectedChange.emit(slot);
  }

  public isCheckedForDuplication(timeslotRow: TimeslotRow): boolean {
    return this.timeslotRowsSelectedForDuplication?.includes(timeslotRow) || false;
  }

  private onError(error: any): void {
    this.errorManagerService.error(error);
    console.error("Calendar : ", error);
  }
}
